/**

 * EncycGames brand tokens — see docs/BRAND_GUIDELINES_ENYCGAMES.md

 */

export const brandColors = {

  navy: '#111739',

  blue: '#4A6FEB',

  orange: '#E8580B',

  violet: '#6116A7',

  green: '#4CB123',

  ice: '#F7F9FC',

  slate: '#465069',

  white: '#FFFFFF',

} as const;



export const brandGradients = {

  contestHeader: `linear-gradient(135deg, ${brandColors.navy} 0%, ${brandColors.navy} 55%, ${brandColors.blue} 100%)`,

  selectedSkill: `linear-gradient(135deg, ${brandColors.navy} 0%, ${brandColors.navy} 70%, ${brandColors.blue} 100%)`,

} as const;



export const ENYCGAMES_LOGO_URL = '/images/EncycGames-Logo1.png';



export const ENCYCDATA_URL = 'https://encycdata.com';



export interface GradeColorStyle {

  bg: string;

  text: string;

  border: string;

  selectedBg: string;

  selectedText: string;

  tier: string;

}



/** Platform grade palette — red → orange → yellow → green → expert purple. */

export const gradeColors: Record<string, GradeColorStyle> = {

  D: {

    tier: 'Beginner',

    bg: '#FEE2E2',

    text: '#B91C1C',

    border: '#FCA5A5',

    selectedBg: 'rgba(254,226,226,0.28)',

    selectedText: '#FECACA',

  },

  F: {

    tier: 'Beginner',

    bg: '#FEE2E2',

    text: '#B91C1C',

    border: '#FCA5A5',

    selectedBg: 'rgba(254,226,226,0.28)',

    selectedText: '#FECACA',

  },

  C: {

    tier: 'Developing',

    bg: '#FFEDD5',

    text: '#C2410C',

    border: '#FDBA74',

    selectedBg: 'rgba(255,237,213,0.28)',

    selectedText: '#FFEDD5',

  },

  B: {

    tier: 'Proficient',

    bg: '#FEF9C3',

    text: '#A16207',

    border: '#FDE047',

    selectedBg: 'rgba(254,249,195,0.28)',

    selectedText: '#FEF9C3',

  },

  A: {

    tier: 'Advanced',

    bg: '#DCFCE7',

    text: '#15803D',

    border: '#86EFAC',

    selectedBg: 'rgba(220,252,231,0.28)',

    selectedText: '#DCFCE7',

  },

  'A+': {

    tier: 'Expert',

    bg: '#EDE9FE',

    text: '#5B21B6',

    border: '#A78BFA',

    selectedBg: 'rgba(237,233,254,0.28)',

    selectedText: '#EDE9FE',

  },

  S: {

    tier: 'Expert',

    bg: '#EDE9FE',

    text: '#5B21B6',

    border: '#A78BFA',

    selectedBg: 'rgba(237,233,254,0.28)',

    selectedText: '#EDE9FE',

  },

};



export function normalizeGradeCode(code: string): string {

  const raw = String(code || '').trim();

  const upper = raw.toUpperCase();

  if (upper === 'A+' || upper === 'A PLUS' || raw === 'A+') return 'A+';

  return upper;

}



export function getGradeColor(code: string): GradeColorStyle {

  const key = normalizeGradeCode(code);

  return gradeColors[key] || gradeColors.B;

}



export function getGradeTierLabel(code: string): string {

  return getGradeColor(code).tier;

}



/** Benchmark gender divisions — Boy/Male, Girl/Female, plus adult Open and Gender diverse. */

export const contestGenderAccents: Record<string, { accent: string; soft: string; tint: string; hover: string }> = {

  boys: { accent: brandColors.blue, soft: '#e8eefc', tint: 'rgba(74,111,235,0.04)', hover: '#3a56c9' },

  girls: { accent: brandColors.violet, soft: '#efe6f8', tint: 'rgba(97,22,167,0.04)', hover: '#4d1290' },

  open: { accent: brandColors.navy, soft: '#e6e8f0', tint: 'rgba(17,23,57,0.04)', hover: '#0a0f28' },

  diverse: { accent: '#0D9488', soft: '#e0f2f0', tint: 'rgba(13,148,136,0.05)', hover: '#0b7a70' },

};



export function getContestGenderAccent(genderKey: string) {

  return contestGenderAccents[genderKey] || contestGenderAccents.boys;

}



/** Journey tab tints — Benchmark, Sports IQ, Learn, Self Evaluation, Contest, Leaderboard. */

export const journeyTabTints = {

  benchmark: {

    bg: '#E6F4F1',

    bgActive: '#0D9488',

    border: '#5EEAD4',

    rail: '#CCFBF1',

  },

  sports_iq: {

    bg: '#FEF9C3',

    bgActive: '#CA8A04',

    border: '#FDE047',

    rail: '#FEF3C7',

  },

  learn: {

    bg: '#E4ECFB',

    bgActive: brandColors.blue,

    border: '#9BB0F0',

    rail: '#C9D6F8',

  },

  self_evaluation: {

    bg: '#F0E6FA',

    bgActive: brandColors.violet,

    border: '#C9A3E8',

    rail: '#E0CCF3',

  },

  contest: {

    bg: '#FFE8DA',

    bgActive: brandColors.orange,

    border: '#F0B08A',

    rail: '#FFD4BC',

  },

  leaderboard: {

    bg: '#E8F6E3',

    bgActive: brandColors.green,

    border: '#A8D99A',

    rail: '#CDEAD0',

  },

} as const;


